import { useEffect } from 'react';
import type { RefObject } from 'react';

interface ScrewAnimationOptions {
  stageRef: RefObject<HTMLDivElement | null>;
  screwRef: RefObject<HTMLDivElement | null>;
  visualRef: RefObject<HTMLDivElement | null>;
  // false (e.g. edit mode) renders the screw docked statically with heat/sparks off
  enabled: boolean;
}

// Port of the template's script.js scroll loop: the single screw image flies from
// the hero, heats up, docks into .product-visual and cools before #ordering.
// Also drives the scroll progress bar and nav/section-dot active states.
export function useScrewAnimation({ stageRef, screwRef, visualRef, enabled }: ScrewAnimationOptions) {
  useEffect(() => {
    const stage = stageRef.current;
    const screwObject = screwRef.current;
    const visual = visualRef.current;
    const progress = document.querySelector<HTMLElement>('.scroll-progress');
    const sections = Array.from(document.querySelectorAll<HTMLElement>('main > section'));
    const navLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('nav a, .section-dots a'));
    const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
    const clamp = (x: number) => Math.max(0, Math.min(1, x));
    const smooth = (x: number) => {
      x = clamp(x);
      return x * x * (3 - 2 * x);
    };

    let disposed = false;
    let frame = 0;
    let lastTime = 0;
    let current = window.scrollY;
    let productTop = 1;
    let orderTop = 1;
    let total = 1;
    let sectionTops: number[] = [];
    let targetBox = { left: 0, top: 0, width: 1, height: 1 };

    function measure() {
      if (disposed) return;
      sectionTops = sections.map(s => s.getBoundingClientRect().top + window.scrollY);
      productTop = document.querySelector<HTMLElement>('#products')?.offsetTop || 1;
      orderTop = document.querySelector<HTMLElement>('#ordering')?.offsetTop || 1;
      if (visual) {
        const r = visual.getBoundingClientRect();
        targetBox = { left: r.left, top: r.top + window.scrollY, width: r.width, height: r.height };
      }
      total = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      schedule();
    }

    function render(time: number) {
      frame = 0;
      if (disposed) return;
      const dt = lastTime ? Math.min(64, time - lastTime) : 16;
      const reduced = motionPreference.matches || !enabled;
      current += (window.scrollY - current) * (reduced ? 1 : 1 - Math.exp(-dt / 75));
      if (Math.abs(current - window.scrollY) < 0.05) current = window.scrollY;
      if (progress) progress.style.transform = `scaleX(${clamp(window.scrollY / total)})`;
      if (sections.length) {
        let active = sections[0].id;
        sectionTops.forEach((top, i) => {
          if (top - window.scrollY <= window.innerHeight * 0.35) active = sections[i].id;
        });
        navLinks.forEach(a => {
          const yes = a.hash === '#' + active;
          a.classList.toggle('active', yes);
          if (yes) a.setAttribute('aria-current', 'location');
          else a.removeAttribute('aria-current');
        });
      }
      if (stage && screwObject) {
        const arrival = Math.max(1, productTop - 90);
        const dockAt = Math.max(arrival, targetBox.top + targetBox.height / 2 - window.innerHeight * 0.5);
        // Camera follows the real viewport; easing scrollY itself makes the fixed
        // image chase the page and drift when the wheel stops or changes direction.
        const cameraProgress = clamp(window.scrollY / dockAt);
        const camera = cameraProgress ** 3 * (cameraProgress * (cameraProgress * 6 - 15) + 10);
        const heating = smooth((current / arrival - 0.28) / 0.72);
        const coolStart = dockAt;
        const coolEnd = Math.max(coolStart + 1, orderTop - window.innerHeight * 0.4);
        const heat = reduced ? 0 : heating * (1 - smooth((current - coolStart) / (coolEnd - coolStart)));
        // One DOM image for the entire journey. Only its camera transform changes.
        const mobile = window.innerWidth <= 600;
        const margin = mobile ? 20 : 32;
        const startX = window.innerWidth * (mobile ? 0.5 : 0.71);
        const startY = window.innerHeight * 0.52;
        // Fit the complete image rectangle, including transparent padding, in the viewport.
        const startWidth = Math.max(
          1,
          Math.min(
            window.innerWidth * (mobile ? 0.94 : 0.68),
            2 * Math.min(startX - margin, window.innerWidth - startX - margin),
            3 * Math.min(startY - margin, window.innerHeight - startY - margin)
          )
        );
        const endWidth = Math.min(targetBox.width, targetBox.height * 1.5);
        const width = startWidth + (endWidth - startWidth) * camera;
        const endX = targetBox.left + targetBox.width / 2;
        const endY = targetBox.top + targetBox.height / 2 - window.scrollY;
        const x = startX + (endX - startX) * camera;
        const y = startY + (endY - startY) * camera;
        if (reduced) {
          screwObject.style.transform = `translate3d(${endX - endWidth / 2}px,${targetBox.top + targetBox.height / 2 - window.scrollY - endWidth / 3}px,0) scale(${endWidth / 1536})`;
        } else {
          screwObject.style.transform = `translate3d(${x - width / 2}px,${y - width / 3}px,0) scale(${width / 1536})`;
        }
        stage.style.setProperty('--heat', String(heat));
        const sparkIn = smooth((current - productTop + window.innerHeight * 0.55) / (window.innerHeight * 0.65));
        const sparkOut = 1 - smooth((current - orderTop + window.innerHeight * 0.75) / (window.innerHeight * 0.6));
        stage.style.setProperty('--sparks', String(reduced ? 0 : sparkIn * sparkOut));
        stage.style.setProperty(
          '--spark-drift',
          `${reduced ? 0 : -smooth((window.scrollY - productTop) / (orderTop - productTop)) * 36}px`
        );
        stage.style.opacity = '1';
      }
      if (current !== window.scrollY) {
        lastTime = time;
        frame = requestAnimationFrame(render);
      } else {
        lastTime = 0;
      }
    }

    function schedule() {
      if (!frame && !disposed) frame = requestAnimationFrame(render);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure);
    window.addEventListener('pageshow', measure);
    motionPreference.addEventListener('change', measure);
    document.fonts?.ready.then(() => measure());
    // Async content (DB values, images) shifts layout after mount; re-measure on DOM changes
    const main = document.querySelector('main');
    const mutationObserver = main ? new MutationObserver(measure) : null;
    mutationObserver?.observe(main as Node, { childList: true, subtree: true });
    measure();

    return () => {
      disposed = true;
      mutationObserver?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
      window.removeEventListener('pageshow', measure);
      motionPreference.removeEventListener('change', measure);
      navLinks.forEach(a => {
        a.classList.remove('active');
        a.removeAttribute('aria-current');
      });
    };
  }, [stageRef, screwRef, visualRef, enabled]);
}
